import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { applyVolumeSettings } from './volumeOutput';
import { VolumeSettings, areVolumeSettingsEqual } from './volumeSettings';
import { selectVolumeSettings, volumeSettingsHydrated } from './volumeSlice';
import { loadVolumeSettings, saveVolumeSettings } from './volumeStorage';

// A slider drag changes the level many times a second; save once it settles.
export const VOLUME_SAVE_DELAY_MS = 300;

type SettingsOwner = { userId: string | undefined; saved: VolumeSettings };
type PendingSave = { timer: ReturnType<typeof setTimeout>; save: () => void };

/**
 * Keeps the volume settings in step with the signed-in user and the audio:
 * loads the user's saved settings into Redux whenever the user changes, applies
 * every change to the audio output, and saves changes to that user's storage.
 * Mount once, near the top of the app.
 */
export function useVolumeSettingsSync(userId: string | undefined): void {
  const dispatch = useDispatch();
  const settings = useSelector(selectVolumeSettings);
  // The user whose settings Redux holds, and what is in their storage. Null
  // while a user's settings are loading, so nothing is saved to the wrong user.
  const ownerRef = React.useRef<SettingsOwner | null>(null);
  const pendingSaveRef = React.useRef<PendingSave | null>(null);

  const flushPendingSave = React.useCallback(() => {
    const pending = pendingSaveRef.current;
    if (!pending) return;
    clearTimeout(pending.timer);
    pendingSaveRef.current = null;
    pending.save();
  }, []);

  React.useEffect(() => {
    flushPendingSave();
    ownerRef.current = null;
    let cancelled = false;
    void loadVolumeSettings(userId).then((loaded) => {
      if (cancelled) return;
      ownerRef.current = { userId, saved: loaded };
      dispatch(volumeSettingsHydrated(loaded));
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch, flushPendingSave, userId]);

  React.useEffect(() => {
    applyVolumeSettings(settings);
  }, [settings]);

  React.useEffect(() => {
    const owner = ownerRef.current;
    if (!owner || areVolumeSettingsEqual(owner.saved, settings)) return;
    if (pendingSaveRef.current) clearTimeout(pendingSaveRef.current.timer);
    const save = () => {
      ownerRef.current =
        ownerRef.current === owner ? { ...owner, saved: settings } : ownerRef.current;
      void saveVolumeSettings(owner.userId, settings);
    };
    pendingSaveRef.current = {
      timer: setTimeout(() => {
        pendingSaveRef.current = null;
        save();
      }, VOLUME_SAVE_DELAY_MS),
      save,
    };
  }, [settings]);

  React.useEffect(() => flushPendingSave, [flushPendingSave]);
}
